/**
 * A tiny History-API router (no dependency).
 *
 *   /            → lobby
 *   /r/:code     → room (join form → waiting room → game)
 *   anything else → lobby
 *
 * `useRoute()` subscribes via useSyncExternalStore; `navigate()` pushes state
 * and notifies subscribers. Room codes are normalised to upper case and
 * validated against the protocol alphabet.
 */
import { useSyncExternalStore } from 'react';
import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from '@wordloom/engine';

export type Route = { name: 'lobby' } | { name: 'room'; code: string };

export type MockMode = 'game' | 'waiting' | 'over' | 'lobby';

/** Normalise a room code; null when it is not a valid code. */
export function normalizeRoomCode(raw: string): string | null {
  const code = raw.trim().toUpperCase();
  if (code.length !== ROOM_CODE_LENGTH) return null;
  for (const ch of code) if (!ROOM_CODE_ALPHABET.includes(ch)) return null;
  return code;
}

/** Parse a pathname into a route. Unknown paths and invalid codes → lobby. */
export function parseRoute(pathname: string): Route {
  const match = /^\/r\/([^/]+)\/?$/.exec(pathname);
  if (match?.[1]) {
    let raw = match[1];
    try {
      raw = decodeURIComponent(raw);
    } catch {
      // Malformed escapes ("/r/%") are simply not a code: fall through to the lobby.
      return { name: 'lobby' };
    }
    const code = normalizeRoomCode(raw);
    if (code) return { name: 'room', code };
  }
  return { name: 'lobby' };
}

/**
 * Accepts what people paste into the "room code" box: a bare code ("k7pqz"),
 * a code with stray spaces/dashes, or a whole invite link
 * ("https://wordloom.example/r/K7PQZ"). Returns the code or null.
 */
export function parseRoomInput(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const fromLink = /\/r\/([A-Za-z0-9]+)/.exec(trimmed);
  if (fromLink?.[1]) return normalizeRoomCode(fromLink[1]);
  return normalizeRoomCode(trimmed.replace(/[\s-]/g, ''));
}

/**
 * Sanitise live typing in the code field: upper-case, drop characters that can
 * never appear in a code, cap at the code length. Pasted links are handled by
 * `parseRoomInput` first.
 */
export function sanitizeCodeTyping(input: string): string {
  let out = '';
  for (const ch of input.toUpperCase()) {
    if (ROOM_CODE_ALPHABET.includes(ch)) out += ch;
    if (out.length === ROOM_CODE_LENGTH) break;
  }
  return out;
}

export function roomPath(code: string): string {
  return `/r/${code.toUpperCase()}`;
}

export function roomUrl(code: string): string {
  return `${window.location.origin}${roomPath(code)}`;
}

/** `?mock=game|waiting|over|lobby` (dev-only design mocks). */
export function parseMockMode(search: string): MockMode | null {
  const value = new URLSearchParams(search).get('mock');
  return value === 'game' || value === 'waiting' || value === 'over' || value === 'lobby' ? value : null;
}

// ---------------------------------------------------------------------------
// History integration
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener('popstate', notify);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener('popstate', notify);
  };
}

let cachedPath = '';
let cachedRoute: Route = { name: 'lobby' };

function getSnapshot(): Route {
  const path = window.location.pathname;
  if (path !== cachedPath) {
    cachedPath = path;
    cachedRoute = parseRoute(path);
  }
  return cachedRoute;
}

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  const url = path + window.location.search;
  if (opts.replace) window.history.replaceState(null, '', url);
  else if (window.location.pathname !== path) window.history.pushState(null, '', url);
  notify();
}

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
